import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '@/api/client';
import type { ClientEntry } from '@/api/types';
import { FocusShell } from '@/components/layout';
import { Alert, Button, Chip, Skeleton } from '@/components/ui';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { NOTE_ID, STRENGTHS_ID, useAssessmentFlow } from '@/features/assessment/useAssessmentFlow';

type ItemEntry = Extract<ClientEntry, { kind: 'item' }>;

/** Locates the queue position of `missing[0]` so an incomplete review can send the parent
 * straight back to the exact card that still needs an answer, rather than to the start. Falls
 * back to the first card if the id can't be found (queue and `missing` came from different
 * fetches, or the id belongs to a non-item entry). */
const firstMissingCardIndex = (queue: ClientEntry[], missing: string[]): number => {
  if (missing.length === 0) return 0;
  const target = missing[0];
  const index = queue.findIndex((entry) => entry.kind === 'item' && entry.id === target);
  return index === -1 ? 0 : index;
};

export const Review = () => {
  const t = useT();
  const navigate = useNavigate();
  const { assessmentId = '' } = useParams<{ assessmentId: string }>();
  const { state } = useAssessmentFlow(assessmentId);
  const { account } = useSession();
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const incomplete = (state.flow?.missing.length ?? 0) > 0;

  // The queue can grow after the parent has already moved past a card (a quick check flags
  // another required item) — if that leaves something unanswered, send them straight back to
  // it instead of letting them submit an incomplete assessment.
  useEffect(() => {
    if (!state.flow || !incomplete) return;
    const index = firstMissingCardIndex(state.flow.queue, state.flow.missing);
    navigate(`/assessments/${assessmentId}?card=${index}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.flow, incomplete, assessmentId]);

  const onSubmit = async () => {
    // Answered without an account: sign up (or log in) first; the finish step then sends it off.
    if (account?.guest) {
      navigate(`/assessments/${assessmentId}/finish?r=1`);
      return;
    }
    setBusy(true);
    setApiError(null);
    try {
      // The review is a required step of the flow: record it, then submit.
      await api.assessments.review(assessmentId);
      await api.assessments.submit(assessmentId);
      navigate(`/sessions/${assessmentId}`);
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      setApiError(t('common.error'));
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <FocusShell onBack={() => navigate(`/assessments/${assessmentId}`)}>
        <Skeleton lines={8} />
      </FocusShell>
    );
  }

  if (!state.flow || !state.child) {
    return (
      <FocusShell onBack={() => navigate('/children')}>
        <Alert tone="warn">{t(state.error ?? 'common.error')}</Alert>
      </FocusShell>
    );
  }

  if (incomplete) {
    return (
      <FocusShell onBack={() => navigate(`/assessments/${assessmentId}`)}>
        <h1 className="text-h1 text-ink">{t('review.title')}</h1>
        <Alert tone="warn" className="mt-4">
          {t('review.incomplete')}
        </Alert>
      </FocusShell>
    );
  }

  const { flow, child, answers } = state;
  const indexedQueue = flow.queue.map((entry, index) => ({ entry, index }));

  const groups = flow.areas
    .map((area) => ({
      area,
      rows: indexedQueue.filter(
        (row): row is { entry: ItemEntry; index: number } =>
          row.entry.kind === 'item' &&
          row.entry.area === area.key &&
          answers[row.entry.id] !== undefined &&
          answers[row.entry.id] !== null,
      ),
    }))
    .filter((group) => group.rows.length > 0);

  const strengthsRow = indexedQueue.find((row) => row.entry.kind === 'strengths');
  const strengthsValues = Array.isArray(answers[STRENGTHS_ID]) ? (answers[STRENGTHS_ID] as string[]) : [];
  const strengthLabel = (value: string) => {
    const entry = strengthsRow?.entry;
    if (entry?.kind !== 'strengths') return value;
    return entry.labels?.[entry.options.indexOf(value)] ?? value;
  };

  const noteRow = indexedQueue.find((row) => row.entry.kind === 'note');
  const noteValue = typeof answers[NOTE_ID] === 'string' ? (answers[NOTE_ID] as string) : '';

  return (
    <FocusShell onBack={() => navigate(`/assessments/${assessmentId}`)}>
      <h1 className="text-h1 text-ink">{t('review.title')}</h1>
      <p className="mt-2 text-body text-ink-muted">{t('review.body', { name: child.name })}</p>

      <div className="mt-6 space-y-6">
        {groups.map((group) => (
          <section key={group.area.key}>
            <h2 className="text-h3 text-ink">{group.area.label}</h2>
            <ul className="mt-3 space-y-3">
              {group.rows.map(({ entry, index }) => (
                <li key={entry.id} className="rounded-md bg-sage-100 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-label text-ink">{entry.text}</p>
                      <p className="mt-1 text-body text-ink-muted">
                        {entry.options[answers[entry.id] as number] ?? ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate(`/assessments/${assessmentId}?card=${index}`)}
                      className="shrink-0 text-label text-teal-600 underline underline-offset-4"
                    >
                      {t('review.edit')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {strengthsRow && strengthsValues.length > 0 && (
          <section>
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-h3 text-ink">{t('q.strengths.title', { name: child.name })}</h2>
              <button
                type="button"
                onClick={() => navigate(`/assessments/${assessmentId}?card=${strengthsRow.index}`)}
                className="shrink-0 text-label text-teal-600 underline underline-offset-4"
              >
                {t('review.edit')}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {strengthsValues.map((value) => (
                <Chip key={value} selected>
                  {strengthLabel(value)}
                </Chip>
              ))}
            </div>
          </section>
        )}

        {noteRow && noteValue && (
          <section>
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-h3 text-ink">{t('q.note.title')}</h2>
              <button
                type="button"
                onClick={() => navigate(`/assessments/${assessmentId}?card=${noteRow.index}`)}
                className="shrink-0 text-label text-teal-600 underline underline-offset-4"
              >
                {t('review.edit')}
              </button>
            </div>
            <p className="mt-3 text-body text-ink">{noteValue}</p>
          </section>
        )}
      </div>

      <p className="mt-8 text-caption text-ink-muted">{t('review.free')}</p>

      {apiError && (
        <div className="mt-3">
          <Alert tone="warn">{apiError}</Alert>
        </div>
      )}

      <Button full loading={busy} onClick={() => void onSubmit()} className="mt-4">
        {t('review.cta')}
      </Button>
    </FocusShell>
  );
};
